import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNotifications, type ApiOptions } from '~/api/helpers';
import {
  CreateDeviceScheduledShowDocument,
  DeleteDeviceScheduledShowDocument,
  RemoveDeviceScheduledShowDocument,
  UpdateDeviceScheduledShowDocument,
} from './schedule.generated';

export const useCreateScheduledShow = (
  options: ApiOptions<typeof CreateDeviceScheduledShowDocument> = {},
) => {
  const { t } = useTranslation();

  return useMutation(CreateDeviceScheduledShowDocument, {
    ...useNotifications(CreateDeviceScheduledShowDocument, {
      failure: t('api.common.error_generic'),
      success: t('api.scheduled_show.create_success_with_devices_plural', {
        count: 1,
      }),
    }),

    ...options,
  });
};

export const useUpdateScheduledShow = (
  options: ApiOptions<typeof UpdateDeviceScheduledShowDocument> = {},
) => {
  const { t } = useTranslation();

  return useMutation(UpdateDeviceScheduledShowDocument, {
    ...useNotifications(UpdateDeviceScheduledShowDocument, {
      failure: t('api.common.error_generic'),
      success: t('api.scheduled_show.update_success'),
    }),

    ...options,
  });
};

export const useDeleteScheduledShow = (
  options: ApiOptions<typeof DeleteDeviceScheduledShowDocument> = {},
) => {
  const { t } = useTranslation();

  return useMutation(DeleteDeviceScheduledShowDocument, {
    ...useNotifications(DeleteDeviceScheduledShowDocument, {
      failure: t('api.common.error_generic'),
      success: (data) => {
        const count = data.deleteScheduledShow?.deletedIds.length ?? 0;
        return t('api.scheduled_show.delete_success_plural', { count });
      },
    }),

    update(cache, { data }) {
      if (!data?.deleteScheduledShow?.deletedIds.length) return;
      data.deleteScheduledShow.deletedIds.forEach((id) =>
        cache.evict({ id: `ScheduledShow:${id}` }),
      );
    },

    ...options,
  });
};

export const useRemoveDeviceFromScheduledShow = (
  options: ApiOptions<typeof RemoveDeviceScheduledShowDocument> = {},
) => {
  const { t } = useTranslation();
  return useMutation(RemoveDeviceScheduledShowDocument, {
    ...useNotifications(RemoveDeviceScheduledShowDocument, {
      failure: t('api.common.error_generic'),
      success: t('api.scheduled_show.update_success'),
    }),
    ...options,
  });
};
